// ShortsMessageId.jsx
import React from 'react';
import { useLoaderData } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import fetchMessageDetail from '../action/fetchMessageDetail.js';
import fetchUser from '../action/fetchUser.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_OBJECT } from '../resource/defaults.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import { prepareVideoPageMeta } from '../resource/prepareVideoPageMeta.js';
import { getVideoFallback } from '../resource/getMediaAsset.js';
import UniversalComponent from '../component/UniversalComponent.jsx';
import useSSRDataMerge, { resetSSRDataMerge } from '../hook/useSSRDataMerge.js';
import getMessageData from '../selector/getMessageData.js';
import shouldSkipSSRFetch from '../resource/shouldSkipSSRFetch.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('ShortsMessageId loader()');
  const errorLog = legacySSRError.extend('ShortsMessageId loader()');
  log('start', { context, params, request });

  // Reset to re-merge SSR data
  resetSSRDataMerge();

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    const skipSSRFetch = shouldSkipSSRFetch({
      requestUrl: new URL(request.url),
      state: store.getState(),
    });
    if (skipSSRFetch) {
      log('skip SSR fetch due to buster query');
      return { data: null, params };
    }

    // Fetch message detail
    await store.dispatch(
      fetchMessageDetail({
        id: params.messageId,
        httpProxyHeaders,
      })
    );

    const state = store.getState();

    // Fetch user detail for SEO meta
    const senderId = getMessageData(state, params.messageId, 'senderId');
    if (senderId) {
      await store.dispatch(
        fetchUser({
          id: senderId,
          httpProxyHeaders,
        })
      );
    }

    const finalState = store.getState();
    const storeData = finalState.toJS();
    log('fetched data', { store: storeData });

    const availableLanguages = getRemoteConfigData(
      finalState,
      SUPPORTED_LANGUAGES
    );
    const canonicalPath = `/shorts/${params.messageId}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareVideoPageMeta({
      state: finalState,
      messageId: params.messageId,
      hostname: request?.headers?.get('host') || 'swag.live',
      canonical,
      alternates,
      includeJsonLd: true,
      forceNoindex: false,
      i18n: i18nInstance,
      contentType: 'shorts',
    });

    log('meta data prepared', { meta });

    const assetIds = getMessageData(finalState, params.messageId, 'assetIds');
    const videoFallback = getVideoFallback({
      messageId: params.messageId,
      assetIds,
    });

    return {
      data: { store: { messages: storeData.messages, users: storeData.users } },
      params,
      meta,
      videoFallback,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const ShortsMessageIdRoute = () => {
  const { data, error, videoFallback } = useLoaderData() || EMPTY_OBJECT;

  useSSRDataMerge({
    componentName: 'ShortsMessageId',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      {videoFallback && (
        <video
          poster={videoFallback.posterUrl}
          preload="none"
          muted
          aria-hidden="true"
          style={seoVideoStyle}
        >
          <source src={videoFallback.contentUrl} type="video/mp4" />
        </video>
      )}
      <UniversalComponent
        filename="Container_ShortsDetail"
        importer={
          /* #__LOADABLE__ */ () => import('../container/ShortsDetail.js')
        }
      />
    </>
  );
};

// 1x1 transparent <video> for SEO crawlers (UniversalComponent won't render in SSR).
// ref: https://developers.google.com/search/docs/appearance/video
const seoVideoStyle = {
  width: 1,
  height: 1,
  opacity: 0,
  position: 'absolute',
  pointerEvents: 'none',
};

export default ShortsMessageIdRoute;
